// Shared part-name label builder.
//
// A part's display name is composed identically in two places:
//   • PartsTrackerPanel rows (from the validation agent's parts)
//   • MultiPartResultView card headers (from the search agent's parts_results)
// Keeping the logic here — pure and React-free — guarantees both render the
// exact same string instead of drifting between two copies.

/** Build the display name for a part using the new schema:
 *  corrected_name, param1Value param2Value … (original_name)
 *  Falls back to the legacy `name` field when the new fields are absent.
 */
export function buildPartDisplayName(p: {
  name?: string | null;
  original_name?: string | null;
  corrected_name?: string | null;
  part_parameters?: string[] | Record<string, string> | null;
}): string {
  const rawBase = p.corrected_name || p.name || p.original_name || '';
  const original = p.original_name || p.name || '';

  // Build a compact parameters string from the dict values (order may vary).
  const rawParams = p.part_parameters
    ? (Array.isArray(p.part_parameters)
        ? p.part_parameters
        : Object.values(p.part_parameters as Record<string, string>))
    : [];
  // The validation schema owns position/side in part_parameters, but model output
  // can occasionally leave the same qualifier in corrected_name as well. Keep a
  // single canonical presentation: core part name first, qualifiers in the suffix.
  // Limit this guard to position/side; other dimensions (for example type:Spoiler)
  // may legitimately be part of the core catalog name.
  const positionalValues = Array.isArray(p.part_parameters)
    ? rawParams.flatMap((raw: string) => {
        const match = String(raw).match(/^\s*(position|side)\s*[:=]\s*(.+?)\s*$/i);
        return match ? [match[2]] : [];
      })
    : Object.entries(p.part_parameters || {}).flatMap(([key, value]) =>
        /^(position|side)$/i.test(key.trim()) ? [String(value)] : [],
      );
  const escapedValues = positionalValues
    .filter(Boolean)
    .sort((a, b) => b.length - a.length)
    .map(value => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const strippedBase = escapedValues.reduce(
    (name, value) => name.replace(new RegExp(`(^|[^A-Za-z0-9])${value}(?=$|[^A-Za-z0-9])`, 'gi'), '$1'),
    rawBase,
  )
    .replace(/\s+/g, ' ')
    .replace(/\s*([,;:/-])\s*$/g, '')
    .replace(/^\s*([,;:/-])\s*/g, '')
    .trim();
  const base = strippedBase || rawBase;
  const paramsStr = rawParams.length
    ? rawParams
        .filter(Boolean)
        .map((s: string) => { const v = s.includes(':') ? s.split(':')[1] : s; return v.charAt(0).toUpperCase() + v.slice(1); })
        .join(', ')
    : '';

  // Compose: "Corrected Name, params (Original Name)"
  // Only append params / original suffix when they add information.
  const suffix: string[] = [];
  if (paramsStr) suffix.push(paramsStr);
  const withParams = suffix.length > 0 ? `${base}, ${suffix.join(', ')}` : base;

  // Resolved name primary, the user's ORIGINAL searched term in parentheses when it
  // differs: "Resolved Name (original)". The backend freezes original_name to the first
  // search, so a clarification answer never overwrites what's shown in the parentheses.
  if (original && original.toLowerCase() !== rawBase.toLowerCase()) {
    return `${withParams} (${original})`;
  }
  return withParams;
}

/** Collapse a qualifier that a "<name>, <qualifiers>" label repeats in its name:
 *  "Rear Bumper Spoiler, Rear" → "Bumper Spoiler, Rear". Language-agnostic (works
 *  on "Para-choques Traseiro, Traseiro" too), so it can tidy backend display labels
 *  persisted before the server stopped composing them this way. Handles both
 *  label shapes — "Label, Rear (original)" and the flipped "original (Label, Rear)"
 *  — by tidying the text outside and inside the outermost parentheses. */
export function dedupeLabelQualifiers(label?: string | null): string {
  const full = (label ?? '').trim();
  if (!full) return '';
  const paren = full.indexOf('(');
  if (paren < 0) return dedupeLabelSegment(full);
  const lastClose = full.lastIndexOf(')');
  if (lastClose < paren) return dedupeLabelSegment(full);
  const head = full.slice(0, paren).trim();
  const inner = full.slice(paren + 1, lastClose);
  const rest = full.slice(lastClose + 1);
  const newHead = head ? dedupeLabelSegment(head) : '';
  const newInner = dedupeLabelQualifiers(inner);
  if (newHead === head && newInner === inner.trim()) return full;
  return `${newHead ? `${newHead} ` : ''}(${newInner})${rest}`;
}

function dedupeLabelSegment(text: string): string {
  const segments = text.split(',').map(s => s.trim());
  const rawCore = segments[0] || '';
  const qualifiers = segments.slice(1).filter(Boolean);
  if (!rawCore || qualifiers.length === 0) return text;
  // A qualifier segment may carry several words ("Rear Lower"); each word is
  // dropped from the name on its own.
  const escaped = qualifiers
    .flatMap(q => q.split(/\s+/))
    .filter(Boolean)
    .sort((a, b) => b.length - a.length)
    .map(q => q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const core = escaped
    .reduce(
      (name, q) => name.replace(new RegExp(`(^|[^\\p{L}\\p{N}])${q}(?=$|[^\\p{L}\\p{N}])`, 'giu'), '$1'),
      rawCore,
    )
    .replace(/\s+/g, ' ')
    .replace(/\(\s+/g, '(')
    .replace(/\s+\)/g, ')')
    .replace(/\s*[,;:/-]\s*$/g, '')
    .replace(/^\s*[,;:/-]\s*/g, '')
    .trim();
  if (!core || core === rawCore) return text;
  return `${core}, ${qualifiers.join(', ')}`;
}

/** The UI-language DISPLAY label for a part. The backend validation agent emits
 *  `display_label` fully in the client language (base + qualifiers translated),
 *  while corrected_name/part_parameters stay in the search language for matching.
 *  We append the user's verbatim original term in parentheses (same convention as
 *  buildPartDisplayName). Returns undefined when the backend sent no display_label,
 *  so callers fall back to buildPartDisplayName (the search-language composition). */
export function buildPartDisplayLabel(p: {
  display_label?: string | null;
  original_name?: string | null;
  name?: string | null;
}): string | undefined {
  const base = dedupeLabelQualifiers(p.display_label);
  if (!base) return undefined;
  const original = (p.original_name || p.name || '').trim();
  if (original && original.toLowerCase() !== base.toLowerCase()) {
    return `${base} (${original})`;
  }
  return base;
}

// ── Part-number request detection ───────────────────────────────────────────
// "SKU 10588708", "OE# A123-456" and bare "10588708" are identifiers, not part
// names. When such a request resolves, every surface (tracker row, results
// table "Requested" column, cart) should lead with the resolved catalog name
// and keep the number as the parenthetical reference — the same
// "Name (original)" convention the label builders already use. Catalog-agnostic:
// the resolved name always comes from the loaded card itself.

/** Leading marker words that flag a part-number request ("SKU 123", "ref: A-1"). */
const PN_MARKER_RE = /^(?:sku|oe|oem|ref|referencia|c[oó]digo|codigo|code|cod|p\/?n|pn|n[uú]mero|numero|num|nro|no|nr|#|№|:)+[\s:.#-]*/i;

/** True when a requested term is a bare part-number/SKU request rather than a
 *  part NAME: an optional marker word followed by a short code that is mostly
 *  digits ("SKU 10588708", "10588708 SEPP", "A123-456"). A term containing a
 *  real word (4+ letters, no digits) is always treated as a name. */
export function isPartNumberQuery(term?: string | null): boolean {
  let s = (term ?? '').trim();
  if (!s) return false;
  for (let i = 0; i < 3; i++) {
    const next = s.replace(PN_MARKER_RE, '').trim();
    if (next === s) break;
    s = next;
  }
  if (s.length < 4) return false;
  const tokens = s.split(/\s+/);
  if (tokens.length > 2) return false;
  const digits = (s.match(/\d/g) || []).length;
  if (digits < 3) return false;
  // A plain word (4+ letters, no digits) means this is a NAME — except a short
  // ALL-CAPS token, which reads as a number-format suffix ("10588708 SEPP").
  if (tokens.some(t => /^[^\d]{4,}$/.test(t) && !/^[A-Z]{4,5}$/.test(t))) return false;
  return tokens.every(t => /^[A-Za-z0-9][A-Za-z0-9\-./]*$/.test(t));
}

/** Split a resolved part label into its display name and the user's original
 *  reference term, which the builders embed as a parenthetical (e.g.
 *  "Friso De Porta, Dianteiro, Esquerdo (friso porta frente esquerdo), Front, Left").
 *  The parenthetical can sit in the middle of the string (qualifiers like
 *  "Front, Left" are appended after it), so we take everything between the FIRST
 *  "(" and the LAST ")" as the ref (handling nested parens) and stitch the
 *  surrounding text back into a clean, comma-tidied part name.
 */
export function splitPartAndRef(full: string): { partName: string; ref: string | null } {
  const tidy = (s: string): string => s
    .replace(/\s+/g, ' ')
    .replace(/\s*,\s*/g, ', ')
    .replace(/(?:,\s*){2,}/g, ', ')        // collapse runs of commas left by the cut
    .replace(/^[\s,]+|[\s,]+$/g, '')       // trim stray leading/trailing commas
    .trim();

  const open = full.indexOf('(');
  const close = full.lastIndexOf(')');
  if (open === -1 || close === -1 || close < open) {
    return { partName: tidy(full), ref: null };
  }
  const ref = full.slice(open + 1, close).trim();
  const partName = tidy(`${full.slice(0, open)} ${full.slice(close + 1)}`);
  return { partName: partName || tidy(full), ref: ref || null };
}

/** The user's ORIGINAL requested line — what they actually typed — for the CSV
 *  "Requested" column and anywhere else that must show the request rather than
 *  the resolved catalog name.
 *
 *  Requested labels are composed as "Resolved Name (original term)", so the
 *  parenthetical IS the user's line; this returns it when present and the label
 *  itself otherwise (a label with no parenthetical is already the raw term).
 *  Defined once here because all three CSV exports — parts results, cart and
 *  order — must agree, and the parts export used to emit the whole composed
 *  label ("Bumper Spoiler, Rear (Para-choques trás (spoiler))"). */
export function requestedOriginalTerm(label?: string | null): string {
  const raw = (label ?? '').trim();
  if (!raw) return '';
  const { partName, ref } = splitPartAndRef(raw);
  return (ref || partName || raw).trim();
}

/** For a part-number request that RESOLVED to a catalog part, compose the
 *  display label "Resolved Name (requested number)". Anything else — a name
 *  request, no resolved name yet, or a number-looking resolved name — passes
 *  the term through unchanged. */
export function composeNumberRequestLabel(term?: string | null, resolvedName?: string | null): string {
  const t = (term ?? '').trim();
  if (!t || !isPartNumberQuery(t)) return t;
  const name = (resolvedName ?? '').trim();
  if (!name || isPartNumberQuery(name)) return t;
  if (name.toLowerCase().includes(t.toLowerCase())) return name;
  return `${name.charAt(0).toUpperCase() + name.slice(1)} (${t})`;
}

// ── Attribute-code expansion ────────────────────────────────────────────────
// Catalog side/hand/position columns are often terse codes (L, RH, RR, UPR…).
// Expand a KNOWN code to its full English words so the extra_details chips read
// clearly — and so the expanded words (not the code) are what gets translated.
// Exact whole-value match only: a code maps to exactly one expansion, and
// anything not in the table (a full word, a part number, a remark sentence) is
// returned unchanged.
const _ATTR_EXPANSIONS: Record<string, string> = {
  // side / hand
  L: 'Left', LH: 'Left', LHD: 'Left', LHS: 'Left', NS: 'Left',
  R: 'Right', RH: 'Right', RHD: 'Right', RHS: 'Right', OS: 'Right',
  // fore / aft
  F: 'Front', FR: 'Front', FRT: 'Front', FRNT: 'Front', FT: 'Front',
  RE: 'Rear', REAR: 'Rear',
  // position + side combos (per convention RR = Rear Right, FL = Front Left, …)
  FRL: 'Front Left', FRR: 'Front Right',
  RL: 'Rear Left', RR: 'Rear Right', RRL: 'Rear Left', RRR: 'Rear Right',
  // vertical
  U: 'Upper', UP: 'Upper', UPR: 'Upper', UPPR: 'Upper',
  LW: 'Lower', LWR: 'Lower', LO: 'Lower', LOW: 'Lower',
  // inner / outer
  IN: 'Inner', INR: 'Inner', INN: 'Inner',
  OUT: 'Outer', OTR: 'Outer', OUTR: 'Outer',
  // center
  CTR: 'Center', CNTR: 'Center', CEN: 'Center',
  // common part words
  ASSY: 'Assembly', ASM: 'Assembly', ASSM: 'Assembly',
};

/** i18n slug for a position word this module synthesized ("Rear Left" → "rearLeft").
 *
 *  These chips are OUR words, not catalog text: `expandAttribute` turns a terse
 *  column code ("L", "RR") into a fixed English vocabulary. Sending them through
 *  the backend description-translation batch is both wasteful and unreliable —
 *  the hero row's `resolvedPositionChips` values are never batched at all, which
 *  is why "Rear"/"Left" stayed English on the recommended row. Callers translate
 *  `chat.position.<slug>` and fall back to the raw word for anything unmapped. */
const _POSITION_SLUGS: Record<string, string> = {
  left: 'left', right: 'right', front: 'front', rear: 'rear',
  'front left': 'frontLeft', 'front right': 'frontRight',
  'rear left': 'rearLeft', 'rear right': 'rearRight',
  upper: 'upper', lower: 'lower', inner: 'inner', outer: 'outer',
  center: 'center', assembly: 'assembly',
};

export function positionChipSlug(value: string): string | null {
  return _POSITION_SLUGS[String(value ?? '').trim().toLowerCase()] ?? null;
}

/** Placeholder attribute values that carry no meaning and must never surface as a
 *  chip (normalized: only A–Z0–9 kept, uppercased). */
const _ATTR_PLACEHOLDERS = new Set(['NA', 'NONE', 'NULL', 'UNDEFINED', 'NIL', 'TBD']);

/** Expand a terse catalog attribute code to full English words (e.g. "RR" →
 *  "Rear Right", "LH" → "Left"). Returns the input unchanged when it isn't a
 *  recognised code (full words, part numbers, remark sentences pass through).
 *  Returns '' for empty / placeholder values ("N/A", "none", "-", …) so every
 *  caller (all of which skip falsy) drops them instead of rendering a dud chip. */
export function expandAttribute(raw: string): string {
  const s = (raw ?? '').trim();
  if (!s) return '';
  const norm = s.toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (!norm || _ATTR_PLACEHOLDERS.has(norm)) return '';   // "-", "—", "N/A", "none", …
  return _ATTR_EXPANSIONS[norm] || s;
}

// ── Side / position chips from extra_details ────────────────────────────────
// The side/position (Left/Right, Front/Rear…) is normally carried in
// part_parameters and folded into the display name. But SOME catalogs put it in
// extra_details under a dedicated column instead — e.g. { "L/R": "L" }. Those
// keys denote a side/position value (not a note or a raw record field), so we
// surface them as position chips. Keys are normalized (lowercased; spaces,
// underscores, hyphens AND slashes stripped) so "L/R" → "lr".
const _POSITION_KEYS = new Set([
  'lr', 'side', 'hand',
  'fr', 'frontrear', 'frontback',
  'ul', 'upperlower', 'updown',
  'io', 'innerouter', 'inout',
  'position', 'pos',
]);

// ── Simple front/rear + left/right classes from a catalog name ──────────────

export interface PositionClasses {
  position: 'front' | 'rear' | null;
  side: 'left' | 'right' | null;
}

/** Best-effort front/rear and left/right classes from a catalog part name or
 *  option label ("PANEL-BODY SI OTR FRT", "GUARDABARROS DELANTERO", "FENDER
 *  (L)"). Token-based port of the backend's `_position_token`/`_side_token`:
 *  no bare "del"/"fr" tokens ("del" is a Spanish preposition, "fr" is
 *  ambiguous), and a dimension with BOTH values present fails open to null. */
export function positionClassesFromName(name: string | null | undefined): PositionClasses {
  const toks = String(name || '').toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
  const front = toks.some(t => t === 'frt' || t === 'front' || t.startsWith('delant') || t.startsWith('dianteir') || t.startsWith('avant'));
  // NOTE: 'rr' reads as REAR (name convention, e.g. "BODYSIDE-RR OTR"), matching
  // the backend — the "Rear Right" reading only applies to attribute columns.
  const rear = toks.some(t => t === 'rr' || t === 'rear' || t === 'tras' || t.startsWith('traser') || t.startsWith('trasei') || t.startsWith('arri'));
  const left = toks.some(t => t === 'l' || t === 'lh' || t === 'ns' || t === 'left' || t.startsWith('izq') || t.startsWith('esquerd'));
  const right = toks.some(t => t === 'r' || t === 'rh' || t === 'os' || t === 'right' || t.startsWith('der') || t.startsWith('direit'));
  return {
    position: front && !rear ? 'front' : rear && !front ? 'rear' : null,
    side: left && !right ? 'left' : right && !left ? 'right' : null,
  };
}

/** Expanded side/position values (e.g. "L" → "Left") pulled from a part's
 *  extra_details for keys that denote a side/position ("L/R", "side", …).
 *  De-duplicated; placeholder/empty values dropped. Returns [] when none. */
/** Catalog names arrive SHOUTING ("TAMPA - DIANTEIRA INFERIOR"); an answer
 *  chip reads as a sentence. Only all-caps labels are touched. */
export function sentenceCase(label: string): string {
  const s = String(label || '').trim();
  if (!s || s !== s.toUpperCase()) return s;
  const lower = s.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

export function positionChipsFromExtra(ed?: Record<string, unknown> | null): string[] {
  if (!ed || typeof ed !== 'object' || Array.isArray(ed)) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const [k, v] of Object.entries(ed)) {
    const key = String(k).toLowerCase().replace(/[\s_\-/]/g, '');
    if (!_POSITION_KEYS.has(key)) continue;
    const val = expandAttribute(String(v ?? '').trim()); // "L" → "Left", drops "N/A"
    const norm = val.toLowerCase();
    if (!val || seen.has(norm)) continue;
    seen.add(norm);
    out.push(val);
  }
  return out;
}

/** Position chips for a part that has been resolved/pinned to a request.
 *
 * EPC descriptions are often deliberately generic ("PANEL ASSEMBLY") and the
 * row may have no side column even though the resolved request is explicitly
 * "Rear Left Quarter Panel". Keep catalog qualifiers authoritative, but fill
 * any dimension the catalog omits from the pinned request/part parameters.
 * This is display context only; it must only be used for the resolved hero row,
 * never to decorate unrelated candidates on the same diagram.
 */
export function resolvedPositionChips(
  extraDetails: Record<string, unknown> | null | undefined,
  requestTexts: Array<string | null | undefined>,
  partParameters?: string[] | Record<string, string> | null,
): string[] {
  const catalog = positionChipsFromExtra(extraDetails);
  const catalogClass = catalog.reduce<PositionClasses>((acc, chip) => {
    const parsed = positionClassesFromName(chip);
    return {
      position: acc.position || parsed.position,
      side: acc.side || parsed.side,
    };
  }, { position: null, side: null });

  const parameterValues = partParameters
    ? (Array.isArray(partParameters) ? partParameters : Object.values(partParameters))
        .map(value => String(value).replace(/^[^:=]+[:=]\s*/, '').trim())
    : [];
  const requested = [...parameterValues, ...requestTexts].reduce<PositionClasses>((acc, text) => {
    const parsed = positionClassesFromName(text);
    return {
      position: acc.position || parsed.position,
      side: acc.side || parsed.side,
    };
  }, { position: null, side: null });

  const out = [...catalog];
  const conflicts = (
    (!!catalogClass.position && !!requested.position && catalogClass.position !== requested.position) ||
    (!!catalogClass.side && !!requested.side && catalogClass.side !== requested.side)
  );
  // Never create a hybrid label from contradictory evidence. The candidate
  // ranking layer should demote this row; if it still renders, show EPC facts
  // only rather than borrowing another qualifier from the request.
  if (conflicts) return out;
  if (!catalogClass.position && requested.position) {
    out.push(requested.position === 'front' ? 'Front' : 'Rear');
  }
  if (!catalogClass.side && requested.side) {
    out.push(requested.side === 'left' ? 'Left' : 'Right');
  }
  return out;
}

// ── Full catalog detail extraction (so users never need the catalog) ─────────

// Internal plumbing keys (IDs / flags / callout refs / part numbers already shown
// elsewhere) — never surfaced. Everything else in extra_details is catalog data
// the user wants to see.
const _INTERNAL_DETAIL_KEYS = new Set([
  'catalogselectionevidence',
  'parentpartid', 'secondarypartid', 'partitemid', 'parentpartitemid', 'partid',
  'calloutlabel', 'paddedcallout', 'callout', 'partcalloutid', 'index',
  'seccrosscatkey', 'crosscatkey', 'alphasortsequence', 'alphasort',
  'addedmanually', 'filtered', 'formattedpartnumber',
  'partnumber', 'oe', 'oenumber', 'sku',
]);
// Already surfaced as OE-note lines / side chips → omit here to avoid duplicates.
const _OE_NOTE_DETAIL_KEYS = new Set(['remark', 'remarks', 'note', 'notes', 'optionalpack', 'optionalpackage']);

/** Keys that must never become a "what tells these two apart" chip.
 *  Internal ids and part numbers differ between ANY two rows, so they separate
 *  nothing a user can act on; production dates and OE notes already have their
 *  own columns and expanders. What remains is the qualifier that actually
 *  distinguishes the parts — side, position, trim, variant. */
export function isDisambiguatingDetailKey(rawKey: string): boolean {
  const norm = String(rawKey).toLowerCase().replace(/[\s_\-/]/g, '');
  if (_INTERNAL_DETAIL_KEYS.has(norm) || _OE_NOTE_DETAIL_KEYS.has(norm)) return false;
  // Dates render as the FROM column; quantity as the ×N badge.
  return !['from', 'to', 'fromdate', 'todate', 'date', 'qty', 'quantity'].includes(norm);
}

const _isPlaceholderValue = (v: string): boolean => {
  const n = v.trim().toLowerCase();
  return !v.trim() || ['n/a', 'na', 'none', 'null', '-', '—', 'undefined'].includes(n);
};

/** Turn a raw catalog key ("optionalPackage", "front_rear", "OEM") into a
 *  readable label ("Optional Package", "Front Rear", "OEM"). */
export function humanizeDetailKey(k: string): string {
  const s = String(k)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')  // camelCase → spaced
    .replace(/[_\-/]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return s.split(' ')
    .map(w => (w.length <= 3 && w === w.toUpperCase()) ? w : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

export interface CatalogDetail { key: string; label: string; value: string; }

/** Canonical form of a catalog detail key ("Part Type"/"part_type" → "parttype").
 *  Callers translate a detail label as `chat.clarification.detail.<norm>`, falling
 *  back to the humanized English when the catalog uses a key we don't know. */
export function normalizeDetailKey(rawKey: string): string {
  return String(rawKey).toLowerCase().replace(/[\s_\-/]/g, '');
}

/** EVERY catalog extra_details entry worth showing the user — so they never have
 *  to open the catalog. Excludes internal plumbing, side/position (shown as
 *  chips) and OE notes (shown as a note line), and drops placeholder values.
 *  Keys are humanized; values are returned raw (translate at the call site). */
export function catalogDetailEntries(ed?: Record<string, unknown> | null): CatalogDetail[] {
  if (!ed || typeof ed !== 'object' || Array.isArray(ed)) return [];
  const out: CatalogDetail[] = [];
  const seen = new Set<string>();
  for (const [k, v] of Object.entries(ed)) {
    const norm = String(k).toLowerCase().replace(/[\s_\-/]/g, '');
    if (_INTERNAL_DETAIL_KEYS.has(norm) || _OE_NOTE_DETAIL_KEYS.has(norm) || _POSITION_KEYS.has(norm)) continue;
    const value = String(v ?? '').trim();
    if (_isPlaceholderValue(value) || seen.has(norm)) continue;
    seen.add(norm);
    out.push({ key: String(k), label: humanizeDetailKey(String(k)), value });
  }
  return out;
}

// ── Guest-mode masked part references ────────────────────────────────────────
// In guest mode the backend replaces every OE part number / part id with an
// opaque token like "ref-a1b2c3d4e5" (see catax_ai/bl/guest_masking.py). The
// token carries no OE information; for display we render it as a mask instead
// of a confusing pseudo-number. Real part numbers pass through unchanged, so
// this is safe to apply unconditionally in shared components.
const MASKED_REF_RE = /^ref-[0-9a-f]{10}$/;

/** Render a part number/id for display — masked ref-tokens become "•••". */
export function displayPartRef(value?: string | null): string {
  const v = String(value ?? '').trim();
  return MASKED_REF_RE.test(v) ? '•••' : v;
}

/** Catalog names arrive SHOUTED ("TRAILING ARM, REAR AXLE, BOTTOM (DITAS)").
 *  Sentence case reads as a description rather than a shelf label. Vendor text
 *  that already has lower case is left alone, and tokens that carry meaning in
 *  caps — codes, sizes, known acronyms — survive. */
const CATALOG_ACRONYMS = new Set([
  'DITAS', 'ES1', 'ES2', 'ABS', 'EBS', 'ASR', 'PTO', 'LHD', 'RHD', 'CPC', 'SAA', 'OE', 'OEM',
]);

// Unicode-aware: Spanish/Portuguese catalog text ("PISTÓN", "RETENCIÓN") must
// lower-case as one word — an ASCII-only token split left "PistÓn".
const HAS_LOWER = new RegExp('\\p{Ll}', 'u');
const UPPER_TOKEN = new RegExp('[\\p{Lu}\\p{N}][\\p{Lu}\\p{N}./-]*', 'gu');
const FIRST_LETTER = new RegExp('^([^\\p{L}]*)(\\p{Ll})', 'u');

export function sentenceCaseCatalog(text: string | null | undefined): string {
  const raw = String(text ?? '').trim();
  if (!raw) return '';
  if (HAS_LOWER.test(raw)) return raw;
  const lowered = raw.replace(UPPER_TOKEN, token => {
    if (/\d/.test(token)) return token;
    if (CATALOG_ACRONYMS.has(token)) return token;
    return token.toLowerCase();
  });
  return lowered.replace(FIRST_LETTER, (_m, lead: string, ch: string) => lead + ch.toUpperCase());
}

/** The description shown on a part row: catalog text in sentence case, with the
 *  position appended when the text does not already say it ("HANDLE BAR" +
 *  Rear -> "Handle bar, rear"). Falls back to the label's own name so a row is
 *  never identified by its number alone. */
export function partDescription(
  catalogText: string | null | undefined,
  labelName: string | null | undefined,
  positions: string[] = [],
): string {
  const base = sentenceCaseCatalog(catalogText) || sentenceCaseCatalog(labelName);
  if (!base) return '';
  const lower = base.toLowerCase();
  const extra = positions
    .map(p => String(p || '').trim())
    .filter(p => p && !lower.includes(p.toLowerCase()));
  return extra.length ? `${base}, ${extra.map(p => p.toLowerCase()).join(', ')}` : base;
}
